const SUBDOMAIN_ITEMS_ENDPOINT = "/public/items/subdomains?per_page=100";

const FIELD_ALIASES = {
  hostnames: ["hostnames", "domains", "domain", "subdomain", "hostname"],
  subdomainQuiz: [
    "subdomainQuiz",
    "subdomain_quiz",
    "quiz_subdomain",
    "quiz_domain",
  ],
  subdomainExpertMode: [
    "subdomainExpertMode",
    "subdomain_expert_mode",
    "expert_subdomain",
    "expert_domain",
  ],
  logo: ["logo"],
  logoBlack: ["logoBlack", "logo_black"],
  logoLabelPNG: ["logoLabelPNG", "logo_label_png", "label_logo"],
  showIndyVitLogo: ["showIndyVitLogo", "show_indyvit_logo"],
  backButtonUrl: ["backButtonUrl", "back_button_url"],
  disableCheckout: ["disableCheckout", "disable_checkout"],
  labelPattern: ["labelPattern", "label_pattern", "blanko_label", "etikett"],
  noIndex: [
    "noIndex",
    "no_index",
    "robotsNoIndex",
    "robots_noindex",
    "noindex",
  ],
  defaultLanguage: [
    "defaultLanguage",
    "default_language",
    "language",
    "sprache",
  ],
  availableLanguages: [
    "availableLanguages",
    "available_languages",
    "languages",
    "sprachen",
  ],
  fixedCapsulasPerDay: [
    "fixedCapsulasPerDay",
    "fixed_capsulas_per_day",
    "capsulas_per_day",
    "kapseln_pro_tag",
  ],
  intakeRecommendation: [
    "intakeRecommendation",
    "intake_recommendation",
    "einnahmeempfehlung",
  ],
  intakeRecommendationMin: [
    "intakeRecommendationMin",
    "intake_recommendation_min",
    "einnahmeempfehlung_min",
  ],
  intakeRecommendationMax: [
    "intakeRecommendationMax",
    "intake_recommendation_max",
    "einnahmeempfehlung_max",
  ],
  productionSheetCapsulas: [
    "productionSheetCapsulas",
    "production_sheet_capsulas",
    "verzehrempfehlung_produktion",
  ],
  productionSheetCapsulasMin: [
    "productionSheetCapsulasMin",
    "production_sheet_capsulas_min",
    "verzehrempfehlung_produktion_min",
  ],
  productionSheetCapsulasMax: [
    "productionSheetCapsulasMax",
    "production_sheet_capsulas_max",
    "verzehrempfehlung_produktion_max",
  ],
  capsuleColor: ["capsuleColor", "capsule_color", "kapselfarbe"],
  capsuleColorHex: ["capsuleColorHex", "capsule_color_hex"],
  capsulaMaterial: ["capsulaMaterial", "capsula_material", "kapselmaterial"],
  fragrance: ["fragrance", "flavor", "geschmack"],
  productionSheetFragrance: [
    "productionSheetFragrance",
    "production_sheet_fragrance",
    "geschmack_produktion",
  ],
};

const normalizeKey = (value) =>
  String(value || "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();

const parseBoolean = (value) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return ["true", "1", "yes", "ja"].includes(value.trim().toLowerCase());
  }

  return Boolean(value);
};

const parseNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const parseList = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (trimmed.startsWith("[")) {
      try {
        return JSON.parse(trimmed)
          .map((entry) => String(entry).trim())
          .filter(Boolean);
      } catch (error) {
        console.warn("Could not parse subdomain list from PIM", error);
      }
    }

    return trimmed
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return [];
};

const parseAssetKey = (value) =>
  String(value || "")
    .trim()
    .split("/")
    .pop()
    .replace(/^blanko_/i, "")
    .replace(/\.(pdf|png)$/i, "")
    .replace(/_label$/i, "")
    .toLowerCase();

const parseLanguageCode = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const itemFields = (item) => {
  const fields = {
    id: item.id,
    name: item.name,
    platform_id: item.platform_id,
    ...(item.data || {}),
  };

  for (const metafield of item.data?.metafields || []) {
    if (metafield?.key) {
      fields[metafield.key] = metafield.value;
    }
  }

  return fields;
};

const findField = (fields, aliases) => {
  const normalizedAliases = aliases.map(normalizeKey);
  const key = Object.keys(fields).find((fieldKey) =>
    normalizedAliases.includes(normalizeKey(fieldKey))
  );
  return key ? fields[key] : undefined;
};

export const normalizeSubdomainItem = (item) => {
  const fields = itemFields(item);
  const normalized = {
    id: item.id,
    name: item.name,
    platform_id: item.platform_id,
  };

  for (const [targetKey, aliases] of Object.entries(FIELD_ALIASES)) {
    const value = findField(fields, aliases);

    if (value === undefined || value === null || value === "") {
      continue;
    }

    if (
      [
        "fixedCapsulasPerDay",
        "intakeRecommendationMin",
        "intakeRecommendationMax",
        "productionSheetCapsulasMin",
        "productionSheetCapsulasMax",
      ].includes(targetKey)
    ) {
      normalized[targetKey] = parseNumber(value);
    } else if (
      ["showIndyVitLogo", "disableCheckout", "noIndex"].includes(targetKey)
    ) {
      normalized[targetKey] = parseBoolean(value);
    } else if (targetKey === "hostnames") {
      normalized[targetKey] = parseList(value);
    } else if (targetKey === "availableLanguages") {
      normalized[targetKey] = parseList(value).map(parseLanguageCode);
    } else if (targetKey === "defaultLanguage") {
      normalized[targetKey] = parseLanguageCode(value);
    } else if (targetKey === "labelPattern") {
      normalized[targetKey] = parseAssetKey(value);
    } else {
      normalized[targetKey] = value;
    }
  }

  normalized.hostnames = [
    ...(normalized.hostnames || []),
    normalized.subdomainQuiz,
    normalized.subdomainExpertMode,
  ].filter(Boolean);

  return normalized;
};

export const findSubdomainConfigForHostname = (items, hostname) => {
  const normalizedHostname = String(hostname || "").toLowerCase();
  const hostnamePrefix = normalizedHostname.split(".")[0];

  return items.map(normalizeSubdomainItem).find((config) => {
    const configuredHostnames = config.hostnames || [];
    const hasHostnameMatch = configuredHostnames.some(
      (entry) => normalizedHostname.indexOf(String(entry).toLowerCase()) === 0
    );
    const hasNameMatch =
      normalizeKey(config.name) === normalizeKey(hostnamePrefix);
    const hasPlatformMatch =
      normalizeKey(config.platform_id) === normalizeKey(hostnamePrefix);

    return hasHostnameMatch || hasNameMatch || hasPlatformMatch;
  });
};

export const loadSubdomainConfig = async (axiosInstance, hostname) => {
  const response = await axiosInstance.get(SUBDOMAIN_ITEMS_ENDPOINT);
  const items = response?.data?.resources || [];

  return findSubdomainConfigForHostname(items, hostname);
};
